import React, { useEffect, useRef, useState } from 'react';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { Button } from './ui/button.js';
import { useDialogA11y } from '../lib/use-dialog-a11y.js';

interface BarcodeScannerProps {
  open: boolean;
  onResult: (text: string) => void;
  onClose: () => void;
  /**
   * Batch mode: keep the camera running after each decode and call
   * `onResult` for every new code. Re-scanning the same code within
   * `BATCH_DEDUPE_MS` is ignored so the scanner doesn't fire repeatedly
   * while the tech holds the camera steady. Footer swaps to a counter +
   * "Done" button. Defaults to false (single-shot, current behaviour).
   */
  batch?: boolean;
  /**
   * Hint text shown below the framed area in batch mode — typically the
   * description of the most recently added item. Caller controls it so
   * the toast can reflect inventory-resolution outcomes.
   */
  batchToast?: { kind: 'ok' | 'warn' | 'error'; text: string } | null;
  /** Number of scans accepted in this batch session. */
  batchCount?: number;
}

type PermState = 'requesting' | 'denied' | 'no-camera' | 'scanning' | 'error';

/** Window during which a repeat of the exact same decoded text is ignored. */
const BATCH_DEDUPE_MS = 2000;

/**
 * Full-screen camera scanner modal. Uses @zxing/browser's
 * BrowserMultiFormatReader (handles 1D + 2D codes) against the user's rear
 * camera. The visual frame + animated scan line match the .pscan pattern
 * in Design/hvac_interactive_field_worker.html.
 *
 * Lifecycle: the reader is created when `open` flips true, and the
 * controls object it returns is stopped on close / unmount. In single-
 * shot mode (default), the first decode closes the modal. In batch mode
 * (`batch=true`) the camera keeps running and fires onResult for every
 * new code, with a 2s dedupe to prevent the same barcode firing twice
 * while the camera is held steady.
 */
export function BarcodeScanner({
  open,
  onResult,
  onClose,
  batch = false,
  batchToast = null,
  batchCount = 0,
}: BarcodeScannerProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const controlsRef = useRef<{ stop: () => void } | null>(null);
  // Stash the latest onResult in a ref so the camera-lifecycle effect can
  // depend ONLY on `open`. Callers (parts-picker, inventory search) tend to
  // pass an inline function for onResult; if we included it in the deps
  // array, every parent re-render would tear down + restart the camera —
  // which manifests as the rear camera flashing on/off and never decoding
  // a barcode (the user-reported "doesn't scan" bug).
  const onResultRef = useRef(onResult);
  useEffect(() => {
    onResultRef.current = onResult;
  }, [onResult]);

  const [state, setState] = useState<PermState>('requesting');
  const [errMsg, setErrMsg] = useState<string | null>(null);
  // Self-gating dialog (`if (!open) return null` below) — pass `open` as
  // `enabled` and keep the hook call above the early return.
  const dialogRef = useDialogA11y<HTMLDivElement>(onClose, open);
  // Batch-mode dedupe: the last decoded text and the timestamp it fired.
  // A repeat of the same text inside BATCH_DEDUPE_MS is dropped so the tech
  // can hold the camera steady on a barcode without it firing twice.
  const lastBatchRef = useRef<{ text: string; at: number } | null>(null);
  // Latest `batch` value in a ref so the decode callback (closed over the
  // initial render) honours toggles without re-mounting the camera.
  const batchRef = useRef(batch);
  useEffect(() => {
    batchRef.current = batch;
  }, [batch]);

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    setState('requesting');
    setErrMsg(null);
    const reader = new BrowserMultiFormatReader();

    (async () => {
      try {
        const video = videoRef.current;
        if (!video) {
          setState('error');
          setErrMsg('Video element not ready');
          return;
        }
        if (typeof navigator === 'undefined' || !navigator.mediaDevices) {
          setState('error');
          setErrMsg('Camera API not available — try a recent Chrome / Safari over HTTPS');
          return;
        }
        // Use facingMode constraints — the browser picks the rear camera
        // natively. The earlier deviceId-enumeration flow misfired on iOS
        // Safari because labels are blank before permission is granted and
        // the back-camera regex fell through to the front camera.
        const constraints: MediaStreamConstraints = {
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
        };
        const controls = await reader.decodeFromConstraints(
          constraints,
          video,
          (result, err) => {
            if (cancelled) return;
            if (result) {
              const text = result.getText();
              if (batchRef.current) {
                // Batch mode: dedupe a repeat of the same text within
                // BATCH_DEDUPE_MS, otherwise fire onResult and keep the
                // camera live. The parent controls when to close.
                const now = Date.now();
                const last = lastBatchRef.current;
                if (last && last.text === text && now - last.at < BATCH_DEDUPE_MS) {
                  return;
                }
                lastBatchRef.current = { text, at: now };
                onResultRef.current(text);
              } else {
                // Single-shot mode: stop on the first hit.
                controls.stop();
                onResultRef.current(text);
              }
            }
            // NotFoundException fires on every empty frame — ignore. Real
            // decode errors (ChecksumException, FormatException) keep
            // scanning silently; the next decoded frame wins.
            if (err && err.name !== 'NotFoundException') {
              // intentional no-op
            }
          },
        );
        if (cancelled) {
          controls.stop();
          return;
        }
        controlsRef.current = controls;
        setState('scanning');
      } catch (e) {
        if (cancelled) return;
        const msg = e instanceof Error ? e.message : String(e);
        const isPermission =
          e instanceof DOMException &&
          (e.name === 'NotAllowedError' || e.name === 'PermissionDeniedError');
        const isNoCamera =
          e instanceof DOMException &&
          (e.name === 'NotFoundError' || e.name === 'OverconstrainedError');
        if (isPermission) {
          setState('denied');
        } else if (isNoCamera) {
          setState('no-camera');
        } else {
          setState('error');
          setErrMsg(msg);
        }
      }
    })();

    return () => {
      cancelled = true;
      controlsRef.current?.stop();
      controlsRef.current = null;
    };
    // Intentionally only `open` — onResult is read via ref above so a
    // parent re-render can't restart the camera mid-decode.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label="Scan a barcode"
      className="fixed inset-0 z-50 flex flex-col bg-slate-950 text-white"
    >
      <div className="flex items-center justify-between p-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">
            {batch ? 'Batch scan' : 'Scan a barcode'}
          </span>
          {batch && batchCount > 0 && (
            <span
              className="text-[10.5px] font-semibold px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-400/50 tabular-nums"
              aria-live="polite"
            >
              {batchCount} added
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md px-3 py-1 text-sm font-medium text-white/90 hover:bg-white/10"
          aria-label={batch ? 'Done scanning' : 'Close scanner'}
        >
          {batch ? `Done${batchCount > 0 ? ` (${batchCount})` : ''}` : 'Close'}
        </button>
      </div>

      <div className="relative flex-1 overflow-hidden">
        <video
          ref={videoRef}
          className="h-full w-full object-cover"
          playsInline
          muted
        />
        {state === 'scanning' && <ScanFrame />}

        {state !== 'scanning' && (
          <div className="absolute inset-0 flex items-center justify-center p-6 text-center">
            {state === 'requesting' && (
              <div className="text-sm text-white/80">Starting camera…</div>
            )}
            {state === 'denied' && (
              <div className="space-y-3">
                <div className="text-base font-medium">Camera access blocked</div>
                <div className="text-sm text-white/70">
                  Allow camera permission in your browser to scan barcodes.
                </div>
                <Button variant="secondary" onClick={onClose}>
                  Close
                </Button>
              </div>
            )}
            {state === 'no-camera' && (
              <div className="space-y-3">
                <div className="text-base font-medium">No camera found</div>
                <div className="text-sm text-white/70">
                  This device doesn't expose a camera the browser can use.
                </div>
                <Button variant="secondary" onClick={onClose}>
                  Close
                </Button>
              </div>
            )}
            {state === 'error' && (
              <div className="space-y-3">
                <div className="text-base font-medium">Scanner failed to start</div>
                <div className="text-sm text-white/70">{errMsg ?? 'Unknown error.'}</div>
                <Button variant="secondary" onClick={onClose}>
                  Close
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {batch && batchToast && (
        <div
          className="mx-3 mb-2 rounded-md px-3 py-2 text-sm flex items-start gap-2"
          style={batchToastStyle(batchToast.kind)}
          aria-live="polite"
        >
          <span className="font-semibold uppercase tracking-wider text-[10px] mt-0.5">
            {batchToast.kind === 'ok' ? 'Added' : batchToast.kind === 'warn' ? 'Check' : 'Failed'}
          </span>
          <span className="flex-1 leading-tight">{batchToast.text}</span>
        </div>
      )}

      <div className="px-4 pb-6 pt-3 text-center text-xs text-white/60">
        {batch
          ? 'Hold each barcode in the frame. Tap Done when finished.'
          : "Point the rear camera at a 1D or 2D barcode. We'll fill the search for you."}
      </div>
    </div>
  );
}

function batchToastStyle(kind: 'ok' | 'warn' | 'error'): React.CSSProperties {
  switch (kind) {
    case 'ok':
      return { backgroundColor: 'rgba(16, 185, 129, 0.15)', color: '#A7F3D0', border: '1px solid rgba(16, 185, 129, 0.5)' };
    case 'warn':
      return { backgroundColor: 'rgba(245, 158, 11, 0.15)', color: '#FCD34D', border: '1px solid rgba(245, 158, 11, 0.5)' };
    case 'error':
      return { backgroundColor: 'rgba(239, 68, 68, 0.15)', color: '#FCA5A5', border: '1px solid rgba(239, 68, 68, 0.5)' };
  }
}

function ScanFrame() {
  return (
    <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
      <div className="relative aspect-[1.6/1] w-[78%] max-w-md rounded-lg border-2 border-emerald-400 bg-emerald-400/5">
        <div className="absolute inset-x-[8%] top-1/2 h-px animate-pulse bg-emerald-400 shadow-[0_0_8px_2px_rgba(29,158,117,0.7)]" />
      </div>
    </div>
  );
}
